// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import { v4 as uuid } from 'uuid';

import { ClientRegistry } from './client/bridge-api-client-registry';
import { client, type ClientId } from './client/bridge-api-client-types';
import { isBroadcastableCommand } from './command/bridge-api-command-guards';
import { CommandInvocation } from './command/bridge-api-command-invocation';
import {
	type BroadcastableCommandConstructor,
	type BroadcastCommandOptions,
	type BroadcastCommandProperties,
	type CommandConstructor,
	type CommandHandler,
	type CommandProperties,
	type CommandResult,
	type ExtractCommandProperties,
	type ExtractCommandResult,
} from './command/bridge-api-command-types';
import {
	DiscoveryCommand,
	ReachableCommand,
	TeardownCommand,
} from './command/commands/discovery-commands';
import { destroyGlobalDebugConsumer } from './debug/bridge-api-debugger';
import { destroyGlobalEventEmitter, emitter } from './events/bridge-events-emitter';
import { BufferedInvocationQueue } from './resiliency/bridge-api-message-queue';
import type { RovoContentBridgeAPIShape } from './rovo-bridge-api-interface';
import {
	createInvocationId,
	isRPCRequest,
	isRPCResponse,
	rpcCommand,
	type RPCIdentifier,
	type RPCMessage,
} from './rpc/bridge-api-rpc-message';
import { RequestHandler } from './rpc/bridge-api-rpc-request-handler';
import { ResponseHandler } from './rpc/bridge-api-rpc-response-handler';
import { type Transport } from './transport/bridge-api-transport-types';

/* eslint-disable no-console */

/**
 * A bridge API which enables bi-directional communication between Rovo chat and immersive content.
 *
 * @description
 * Implements the following high level requirements to enable simple DevX with baked-in resiliency:
 *
 * 1) Uses the command pattern for strongly-typed invocation of methods
 * 2) Clients are able to register themselves as handling a specific command
 * 3) Clients can send command instances, with a `senderId` and `receiverId` for routing purposes
 * 4) If a client attempts to send a message to a client which is not ready yet, it will be buffered & tried again later; if
 * 		the recipient client takes too long to become ready, messages buffered agianst it will be dropped. See {@link BufferedInvocationQueue}.
 * 5) Clients exchange {@link DiscoveryCommand} and {@link ReachableCommand} to perform a 'connection handshake' on first attempt
 *  	at communicating with one another. This is to lazily establish context in bridge API instances around which clients a specific
 * 		instance can communicate with (point-to-point), rather than elaborate broadcasting schemes across JS contexts.
 * 6) Multiple command handlers can be implemented for a command; but, only one handler is allowed per command per clientID, to ensure
 * 		we can return the result of invoking a command with a specific `receiverId` without issue. See {@link ResponseHandler} for more.
 * 7) Commands are passed over-the-wire if crossing between JS contexts using the JSON RPC 2.0 protocol.
 * 		See {@link https://www.jsonrpc.org/specification} for more.
 *
 * @example
 * ```typescript
 * 	const api = getBrowserBridgeAPI();
 * 	const myApp = client('my-app');
 * 	const myChat = client('my-chat');
 *
 * 	// context 1 (my-chat) registers a handler
 * 	api.on(myChat, MyCommand, (params) => params.name.toUpperCase());
 *
 * 	// context 2 (my-app) sends the command
 * 	const invocation = api.send(MyCommand, {
 * 		name: 'my-app',
 * 		senderId: myApp,
 * 		receiverId: myChat
 * 	});
 *
 * 	// Get the result
 * 	const result = await invocation.result();
 * 	console.log(result); // expected: 'MY-APP'
 *
 * 	// Send a time-sensitive command that shouldn't be buffered
 * 	const urgentInvocation = api.send(UrgentCommand, {
 * 		senderId: myApp,
 * 		receiverId: myChat,
 * 		buffer: false // Will resolve to undefined if receiver not ready
 * 	});
 *
 * 	// Or cancel the command if needed
 * 	// invocation.cancel();
 * ```
 */
export class RovoContentBridgeAPI implements RovoContentBridgeAPIShape {
	private transport: Transport;
	private identifier: ClientId;

	private registry = new ClientRegistry();
	private queue = new BufferedInvocationQueue();
	private rpc: { request: RequestHandler; response: ResponseHandler };

	constructor(instanceId: string | undefined, transport: Transport) {
		this.transport = transport;
		// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
		this.identifier = client(instanceId ? instanceId : `bridge-api-${uuid()}`);
		this.rpc = {
			request: new RequestHandler(this.transport),
			response: new ResponseHandler(),
		};

		// setup emitter for all events
		emitter().setBridgeId(this.identifier);

		// start listening for any incoming messages
		this.transport.onMessage(this.onMessage.bind(this));

		// setup tick handler for sending discovery commands to pending clients
		this.queue.onTick((pendingConnections) => {
			for (const { senderId, receiverId } of pendingConnections) {
				this.sendCommand(DiscoveryCommand, { senderId, receiverId });
			}
		});

		// setup dropped message handler to cancel orphaned promises
		this.queue.onInvocationDroppped((m) => {
			this.rpc.response.cancel(m.invocationId);

			emitter().emit({
				type: 'message-dropped',
				messageCount: this.queue.messageCount,
				messageId: m.invocationId,
				message: { ...m.params },
				clientId: m.senderId,
				reason: 'queue-overflow',
			});
		});

		// setup client added handler to drain commands when clients are ready
		this.registry.onClientAdded((clientId) => {
			this.drainCommands(clientId);
		});

		// observe readiness of bridge API
		emitter().emit({ type: 'bridge-ready' });
	}

	/**
	 * Unique ID for this specific bridge API instance.
	 *
	 * @description Should be the 1 instance in a given JS context.
	 */
	get id(): ClientId {
		return this.identifier;
	}

	/**
	 * Indicates a specific client is ready to start sending and accepting messages.
	 *
	 * @param clientId unique identifier for the client.
	 */
	ready(clientId: ClientId): void {
		this.registry.addLocalClient(clientId);

		// client-specific handlers - sent by a remote client -> this bridge API instance,
		// targeting the client which we are setting up in this method
		this.createDiscoveryHandler(clientId);
		this.createReachableHandler(clientId);
		this.createTeardownHandler(clientId);

		// emit client registration event
		emitter().emit({ type: 'client-registered', clientId });
	}

	/**
	 * Send a command with strongly-typed parameters to a recipient.
	 *
	 * @description It is possible to wait for the response of a command via the following:
	 * ```typescript
	 * const command = send(MyCommand, {});
	 * await command.result(); // promise with result of the command
	 * // optionally, to cancel:
	 * command.cancel();
	 * ```
	 * Command invocations should be treated similarly in concept to network requests to an upstream system.
	 * The upstream system can time out, disappear, crash, etc. Hence, all command results yield an optional
	 * result, with resiliency baked in for connectivity with a client see {@link BufferedInvocationQueue}.
	 *
	 * Deferrables are used to expose a stable promise API for the result of a command. I.e., if the receiver
	 * of a command is not seen as 'ready' yet, commands will be buffered until a time limit. Once that time
	 * limit is expended, the message will be dropped, and its associated deferrable cancelled.
	 *
	 * @returns {CommandInvocation} api for handling the response of the command.
	 */
	send<T extends CommandConstructor, R extends CommandResult = ExtractCommandResult<T>>(
		command: T,
		params: ExtractCommandProperties<T>,
	): CommandInvocation<R> {
		// assign a unique ID to this command invocation
		if (!params._invocationId) {
			// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
			params._invocationId = uuid();
		}

		// unique, stable ID for this command
		const id = createInvocationId(command, params);
		const message = rpcCommand(id, command, params);

		const senderId = params.senderId;
		const receiverId = params.receiverId;

		// emit command creation event
		emitter().emit({
			type: 'command-created',
			commandId: id,
			commandType: command.identifier,
			senderId,
			receiverId,
			properties: { ...params },
			groupId: params.groupId,
		});

		let promise: Promise<R | undefined>;

		// not ready to send to a known, communicable client yet - buffer & defer (if allowed)
		if (
			!this.registry.has(receiverId) ||
			!this.transport.canSend(message) ||
			this.registry.isPendingClient(receiverId)
		) {
			// default to buffering the sending of all commands
			const buffer = params.buffer ?? true;
			if (buffer) {
				this.queue.enqueue(id, senderId, receiverId, command, params);
				promise = this.rpc.response.getOrCreate(id, senderId, receiverId);
			} else {
				// opted out of buffering - resolve immediately to undefined
				promise = Promise.resolve(undefined);
			}
		}
		// ready to send to local client - immediately invoke
		else if (this.registry.isLocalClient(receiverId)) {
			// emit command sent event for local invocation
			emitter().emit({
				type: 'command-sent',
				commandId: id,
				commandType: command.identifier,
				senderId,
				receiverId,
				properties: params,
				groupId: params.groupId,
			});

			const result = this.rpc.request.invoke(command, params);
			const existing = this.rpc.response.get(id);

			// if a local client was not ready yet and we tried to communicate with it,
			// we do not want to instantiate a new promise. Hence, we need to latch onto
			// the same deferrable as before, and yield it with the result of invoking the
			// handler provided by the local client which is ready & we are communicating to
			if (existing) {
				existing.resolve(result);
				promise = existing.promise as Promise<R>;
			} else {
				promise = result as Promise<R>;
			}
		}
		// ready to send to remote client - use transport
		else {
			// emit command sent event
			emitter().emit({
				type: 'command-sent',
				commandId: id,
				commandType: command.identifier,
				senderId,
				receiverId,
				properties: params,
				groupId: params.groupId,
			});

			this.transport.send(message);
			promise = this.rpc.response.getOrCreate(id, senderId, receiverId);
		}

		return new CommandInvocation(
			id,
			promise,
			(invocationId) => this.cancel(invocationId),
			(groupId) => this.cancelGroup(groupId),
		);
	}

	/**
	 * Broadcasts a command to all registered clients except those that are pending or filtered out.
	 *
	 * @typeParam C - The type of command properties, which must include a `receiverId` of type `ClientId`.
	 * @param command - The constructor for the broadcastable command to be sent.
	 * @param params - The parameters for the command, excluding the `receiverId` which will be set for each recipient.
	 * @param options - Optional broadcast options, including a filter function to select specific clients.
	 *
	 * @remarks
	 * - Only commands that pass `isBroadcastableCommand` are sent.
	 * - The method retrieves all client IDs from the registry, filters out pending clients, and applies any additional filter provided in `options`.
	 * - For each eligible client, the command is sent with the appropriate `receiverId`.
	 * - Errors during broadcasting are caught and logged to the console.
	 */
	broadcast<C extends CommandProperties & { receiverId: ClientId }>(
		command: BroadcastableCommandConstructor<C>,
		params: BroadcastCommandProperties<C>,
		options?: BroadcastCommandOptions,
	): void {
		try {
			if (!isBroadcastableCommand(command)) {
				return;
			}

			const receiverIds =
				this.registry
					.getAllClientIds()
					?.filter(
						(clientId) =>
							!this.registry.isPendingClient(clientId) && String(params.senderId) !== clientId,
					)
					?.filter((clientId) => (options?.filter ? options.filter(clientId) : true)) ?? [];

			for (const receiverId of receiverIds) {
				this.send(command, { ...params, receiverId } as C);
			}

			if (receiverIds.length) {
				emitter().emit({
					type: 'command-broadcast',
					senderId: params.senderId as ClientId,
					receiverIds,
					commandType: command.identifier,
				});
			}
		} catch (err) {
			console.error('broadcast failed', err);
		}
	}

	/**
	 * Register a handler for a specific command type for a specific client.
	 */
	on<
		T extends CommandConstructor,
		C extends CommandProperties = ExtractCommandProperties<T>,
		R extends CommandResult = ExtractCommandResult<T>,
	>(clientId: ClientId, Command: T, handler: CommandHandler<C, R>): void {
		this.rpc.request.register(clientId, Command, handler);

		emitter().emit({
			type: 'handler-registered',
			clientId,
			commandType: Command.identifier,
		});
	}

	/**
	 * Unregister a handler for a specific command type for a specific client.
	 */
	off<T extends CommandConstructor>(clientId: ClientId, Command: T): void {
		this.rpc.request.unregister(clientId, Command);

		emitter().emit({
			type: 'handler-unregistered',
			clientId,
			commandType: Command.identifier,
		});
	}

	/**
	 * Tears down a specific client, removing it from the registry and notifying all remote clients.
	 *
	 * @description This method performs the following actions:
	 * 1. Removes the client from the local registry
	 * 2. Sends teardown commands to all known remote clients
	 * 3. Drops all buffered messages for the client
	 * 4. Unregisters all command handlers for the client
	 * 5. Clears any pending response handlers for the client
	 * 6. Removes transport target references for the client
	 *
	 * @param clientId unique identifier for the client to tear down
	 */
	teardown(clientId: ClientId): void {
		// 1. remove the client from the registry
		const wasRemoved = this.registry.removeClient(clientId);
		if (!wasRemoved) {
			return;
		}

		// 2. notify all remote clients about the teardown
		const allClientIds = this.registry.getAllClientIds();
		for (const otherClientId of allClientIds) {
			// irrespective of state, we should communicate we are tearing down
			// this handles any potential race conditions with in-flight messages
			// across JS contexts (e.g., actively shaking hands with a pending client)
			if (
				this.registry.isPendingClient(otherClientId) ||
				this.registry.isRemoteClient(otherClientId)
			) {
				this.sendCommand(TeardownCommand, {
					senderId: clientId,
					receiverId: otherClientId,
				});
			}
		}

		// 3. drop all buffered messages for this client
		this.queue.clear(clientId);

		// 4. unregister all command handlers for this client
		this.rpc.request.unregisterAll(clientId);

		// 5. clear any pending response handlers for this client
		this.rpc.response.teardown(clientId);

		// 6. remove transport target references (Window/WebContents) for this client
		this.transport.teardown(clientId);

		// 7. emit client teardown event
		emitter().emit({ type: 'client-teardown', clientId });
	}

	/**
	 * Clear all state prior to the application being destroyed.
	 *
	 * @description NOTE: Ensure all containing state is destroyed to avoid memory leaks during
	 * SPA transitions away from application contexts which use the bridge API.
	 */
	destroy(): void {
		this.queue.destroy();
		this.rpc.request.destroy();
		this.rpc.response.clear();
		this.registry.destroy();
		this.transport.destroy();

		// stop emitting any events & drop memory
		emitter().emit({ type: 'bridge-destroyed' });

		// clean up all event emitter state
		destroyGlobalEventEmitter();
		destroyGlobalDebugConsumer();
	}

	/**
	 * Registers a message listener for RPC events received over {@link transport}.
	 * @param data RPC message received by this JS context
	 */
	private async onMessage(data: RPCMessage) {
		try {
			if (isRPCRequest(data)) {
				return await this.rpc.request.handleRequest(data);
			}

			if (isRPCResponse(data)) {
				return this.rpc.response.resolveWithResponse(data);
			}
		} catch (err) {
			console.error('failed to handle message:', err);
		}
	}

	/**
	 * Sets up a reachable handler for a specific client ID
	 * @summary the {@link senderId} is the remote client who sent this command, expressing they are now reachable.
	 * @param senderId
	 */
	private createReachableHandler(senderId: ClientId) {
		this.on(senderId, ReachableCommand, (params) => {
			this.registry.addRemoteClient(params.senderId);

			emitter().emit({
				type: 'client-discovered',
				clientId: params.senderId,
				metadata: {
					discoveryMethod: 'reachable-command',
					localClientId: senderId,
				},
			});
		});
	}

	/**
	 * Sets up a discoverable handler for a specific client ID
	 * @summary the {@link senderId} is the remote client who sent this command, expressing they are looking for {@link senderId}.
	 * @param senderId
	 */
	private createDiscoveryHandler(senderId: ClientId) {
		this.on(senderId, DiscoveryCommand, (params) => {
			this.sendCommand(ReachableCommand, {
				senderId: params.receiverId,
				receiverId: params.senderId,
			});
			this.registry.addRemoteClient(params.senderId);

			emitter().emit({
				type: 'client-discovered',
				clientId: params.senderId,
				metadata: {
					discoveryMethod: 'discovery-command',
					localClientId: senderId,
				},
			});
		});
	}

	/**
	 * Sets up a teardown handler for a specific client ID
	 * @summary the {@link senderId} is the remote client who sent this command, expressing they are shutting down.
	 * @param receiverId the local client that should handle teardown notifications
	 */
	private createTeardownHandler(receiverId: ClientId) {
		this.on(receiverId, TeardownCommand, (params) => {
			// remove the client that is tearing down from our registry
			this.registry.removeClient(params.senderId);

			// drop all buffered messages for the client that is tearing down
			this.queue.clear(params.senderId);
		});
	}

	/**
	 * Immediately send a command over the configured {@link transport}.
	 *
	 * @description Used for handshake between clients, to simplify logic.
	 * @description Re-used to send all commands in the bridge API.
	 *
	 * @param command command to send
	 * @param params command properties to provide
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private sendCommand(command: CommandConstructor<any, any>, params: CommandProperties) {
		const id = createInvocationId(command, params);
		const message = rpcCommand(id, command, params);

		this.transport.send(message);
	}

	/**
	 * Drain commands for a given client ID from the queue.
	 * @param clientId unique identifier for the client
	 */
	private drainCommands(clientId: ClientId) {
		const messages = this.queue.flush(clientId);
		if (!messages) {
			return;
		}

		for (const message of messages) {
			this.send(message.command, message.params);
		}
	}

	/**
	 * Cancel all commands in a specific group.
	 *
	 * @param groupId The group identifier of commands to cancel
	 * @returns number of commands that were cancelled
	 */
	private cancelGroup(groupId: string): number {
		// remove the buffered commands with this group ID from the queue, if they exist; will also clean up
		// the RPC response handler deferrable for each command
		return this.queue.cancelGroup(groupId);
	}

	/**
	 * Cancel a specific command invocation by its ID.
	 *
	 * @param invocationId The unique identifier of the command invocation to cancel
	 * @returns true if the command was found and cancelled, false otherwise
	 */
	private cancel(invocationId: RPCIdentifier): boolean {
		// remove the buffered command from the queue, if it exists on it; this will also clean up
		// the RPC response handler deferrable for this command
		return this.queue.cancel(invocationId);
	}
}
